import { APP_ROUTES } from '@/const/appRoutes';
import { useSubscriptionAccessStore, type SubscriptionErrorData } from '@/stores/subscriptionAccess';

const FALLBACK_MESSAGE = 'Subscription required to access this resource';
const DEFAULT_ACTION = 'Subscribe Now';
const REDIRECT_DELAY_MS = 100;

/**
 * Checks if error is subscription/tuition-related.
 * Handles both response structures: { detail: { error: "..." } } and { error: "..." }.
 * Backend uses tuition_* codes (e.g. tuition_canceled).
 */
export const isSubscriptionError = (error: any): boolean => {
  if (!error.response) return false;

  const { status, data } = error.response;

  // Check for 402 Payment Required
  if (status === 402) return true;

  // Check for 403 Forbidden with tuition error
  if (status === 403) {
    const errorCode = data?.detail?.error || data?.error;
    return typeof errorCode === 'string' && errorCode.startsWith('tuition_');
  }

  return false;
};

/**
 * Creates subscription error object from API response
 */
export const createSubscriptionError = (detail: any, fallbackMessage: string): SubscriptionErrorData => {
  return {
    error: detail?.error || 'subscription_required',
    message: detail?.message || fallbackMessage,
    action: detail?.action || DEFAULT_ACTION,
    action_url: detail?.action_url || APP_ROUTES.TUITION,
    status: detail?.status,
    subscription: detail?.subscription || null
  };
};

/**
 * Redirects to permission-denied page
 */
const redirectToPermissionDenied = (): void => {
  const redirectUrl = APP_ROUTES.PERMISSION_DENIED;
  
  setTimeout(() => {
    if (window.location.pathname !== redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, REDIRECT_DELAY_MS);
};

/**
 * Handles subscription error by storing it in Pinia store and redirecting to permission-denied page
 * Handles both response structures: { detail: {...} } and direct { error, message, ... }
 */
export const handleSubscriptionError = (error: any): void => {
  const data = error.response?.data;
  const detail = data?.detail || data; // Support both response structures
  
  const subscriptionError = createSubscriptionError(detail, FALLBACK_MESSAGE);
  
  // Store error details in Pinia store
  const subscriptionAccessStore = useSubscriptionAccessStore();
  subscriptionAccessStore.setError(subscriptionError);

  // Tuition pages are exempt from the access gate (see
  // shouldSkipPlatformAccessCheck) so users can fix billing, e.g. retry a
  // failed payment. Their layout still fires gated API calls (403 tuition_*);
  // redirecting on those would trap the user in a loop between
  // /permission-denied and the retry page.
  if (window.location.pathname.startsWith(APP_ROUTES.TUITION)) {
    return;
  }

  // Redirect to permission-denied page
  redirectToPermissionDenied();
};

